<script lang="ts">
  import type { MediaGridBlock, MediaGridItem } from './types';
  import { onMount, onDestroy } from 'svelte';
  import { Markdown } from '@lib/components';

  export let block: MediaGridBlock;

  let currentSlide = 0;
  let rotationInterval: ReturnType<typeof setInterval> | null = null;
  let gridElement: HTMLElement;

  const defaultColumns = {
    mobile: 3,
    tablet: 4,
    desktop: 6
  };

  const columns = {
    mobile: block.config?.columns?.mobile ?? defaultColumns.mobile,
    tablet: block.config?.columns?.tablet ?? defaultColumns.tablet,
    desktop: block.config?.columns?.desktop ?? defaultColumns.desktop
  };

  const itemsPerSlide = block.config?.itemsPerSlide || {
    mobile: columns.mobile * 2,
    tablet: columns.tablet * 2,
    desktop: columns.desktop * 2
  };

  const isCarousel = block.config?.variant === 'carousel';
  const gap = block.config?.gap || '1rem';

  $: totalSlides = isCarousel ? Math.ceil(block.items.length / getItemsPerSlide()) : 1;
  $: visibleItems = isCarousel ? getVisibleItems(currentSlide) : block.items;

  function getItemsPerSlide(): number {
    if (typeof window === 'undefined') return itemsPerSlide.desktop ?? defaultColumns.desktop * 2;

    const width = window.innerWidth;
    if (width < 640) return itemsPerSlide.mobile ?? defaultColumns.mobile * 2;
    if (width < 1024) return itemsPerSlide.tablet ?? defaultColumns.tablet * 2;
    return itemsPerSlide.desktop ?? defaultColumns.desktop * 2;
  }

  function getVisibleItems(slide: number): MediaGridItem[] {
    const itemsCount = getItemsPerSlide();
    const start = slide * itemsCount;
    const end = start + itemsCount;
    return block.items.slice(start, end);
  }

  function nextSlide() {
    currentSlide = (currentSlide + 1) % totalSlides;
  }

  function previousSlide() {
    currentSlide = (currentSlide - 1 + totalSlides) % totalSlides;
  }

  function startAutoRotate() {
    if (block.config?.autoRotate && isCarousel) {
      rotationInterval = setInterval(nextSlide, block.config.autoRotate);
    }
  }

  function stopAutoRotate() {
    if (rotationInterval) {
      clearInterval(rotationInterval);
      rotationInterval = null;
    }
  }

  function handleResize() {
    // Force re-calculation of visible items on resize
    if (isCarousel) {
      currentSlide = 0;
      visibleItems = getVisibleItems(currentSlide);
    }
  }

  onMount(() => {
    startAutoRotate();
    window.addEventListener('resize', handleResize);
  });

  onDestroy(() => {
    stopAutoRotate();
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', handleResize);
    }
  });

  function getCssString(): string {
    if (typeof block.css === 'string') return block.css;
    if (typeof block.css === 'object') {
      const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
      if (width < 640 && block.css.mobile) return block.css.mobile;
      if (width < 1024 && block.css.tablet) return block.css.tablet;
      if (block.css.desktop) return block.css.desktop;
    }
    return '';
  }

  function getMaxWidth() {
    if (!block.layout?.width) return '100%';
    if (block.layout.width === 'full') return '100%';
    return `${block.layout.width}px`;
  }

  $: customStyles = getCssString();
  $: maxWidth = getMaxWidth();
  $: alignment = block.alignment || 'center';
</script>

<div
  class="media-grid-block"
  class:full-width={block.layout?.width === 'full'}
  class:carousel-mode={isCarousel}
  bind:this={gridElement}
  style="
    --gap: {gap};
    --mobile-columns: {columns.mobile};
    --tablet-columns: {columns.tablet};
    --desktop-columns: {columns.desktop};
    --max-width: {maxWidth};
    {customStyles}
  "
>
  {#if block.title || block.subtitle || block.content}
    <div class="media-grid-header align-{alignment}">
      {#if block.title}
        <h2 class="media-grid-title">{block.title}</h2>
      {/if}
      {#if block.subtitle}
        <p class="media-grid-subtitle">{block.subtitle}</p>
      {/if}
      {#if block.content}
        <div class="media-grid-content">
          {#if block.markdown}
            <Markdown content={block.content} align={alignment || 'center'} />
          {:else}
            <p>{block.content}</p>
          {/if}
        </div>
      {/if}
    </div>
  {/if}

  <div class="media-grid-container">
    <div class="media-grid" class:align-center={alignment === 'center'}>
      {#each visibleItems as item}
        <div class="media-grid-item" style="transform: scale({item.scale || 1})">
          {#if item.href}
            <a href={item.href} target="_blank" rel="noopener noreferrer" class="media-link">
              {#if item.type === 'image'}
                <picture>
                  {#if (item as any).sources}
                    {#each (item as any).sources as source}
                      <source media={source.media} srcset={source.srcset} />
                    {/each}
                  {/if}
                  <img
                    src={item.url || `/images/${item.filename}.webp`}
                    alt={item.alt || ''}
                    class="media-image"
                    style="
                      object-fit: {item.objectFit || 'contain'};
                      {item.objectPosition ? `object-position: ${item.objectPosition};` : ''}
                      {item.aspectRatio ? `aspect-ratio: ${item.aspectRatio};` : ''}
                    "
                  />
                </picture>
              {:else if item.type === 'video'}
                <video
                  src={item.url || `/videos/${item.filename}.mp4`}
                  autoplay={item.autoPlay}
                  muted={item.mute}
                  loop={item.loop}
                  controls={item.controls}
                  class="media-video"
                  style="
                    object-fit: {item.objectFit || 'contain'};
                    {item.objectPosition ? `object-position: ${item.objectPosition};` : ''}
                    {item.aspectRatio ? `aspect-ratio: ${item.aspectRatio};` : ''}
                  "
                >
                  <track kind="captions" />
                </video>
              {/if}
            </a>
          {:else}
            {#if item.type === 'image'}
              <picture>
                {#if (item as any).sources}
                  {#each (item as any).sources as source}
                    <source media={source.media} srcset={source.srcset} />
                  {/each}
                {/if}
                <img
                  src={item.url || `/images/${item.filename}.webp`}
                  alt={item.alt || ''}
                  class="media-image"
                  style="
                    object-fit: {item.objectFit || 'contain'};
                    {item.objectPosition ? `object-position: ${item.objectPosition};` : ''}
                    {item.aspectRatio ? `aspect-ratio: ${item.aspectRatio};` : ''}
                  "
                />
              </picture>
            {:else if item.type === 'video'}
              <video
                src={item.url || `/videos/${item.filename}.mp4`}
                autoplay={item.autoPlay}
                muted={item.mute}
                loop={item.loop}
                controls={item.controls}
                class="media-video"
                style="
                  object-fit: {item.objectFit || 'contain'};
                  {item.objectPosition ? `object-position: ${item.objectPosition};` : ''}
                  {item.aspectRatio ? `aspect-ratio: ${item.aspectRatio};` : ''}
                "
              >
                <track kind="captions" />
              </video>
            {/if}
          {/if}
        </div>
      {/each}
    </div>

    {#if isCarousel && totalSlides > 1}
      <div class="carousel-controls">
        <button
          class="carousel-control carousel-control-prev"
          on:click={previousSlide}
          on:mouseenter={stopAutoRotate}
          on:mouseleave={startAutoRotate}
          aria-label="Previous slide"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <button
          class="carousel-control carousel-control-next"
          on:click={nextSlide}
          on:mouseenter={stopAutoRotate}
          on:mouseleave={startAutoRotate}
          aria-label="Next slide"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>

      <div class="carousel-indicators">
        {#each Array(totalSlides) as _, index}
          <button
            class="carousel-indicator"
            class:active={currentSlide === index}
            on:click={() => currentSlide = index}
            aria-label="Go to slide {index + 1}"
          ></button>
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  .media-grid-block {
    position: relative;
    width: 100%;
    max-width: var(--max-width);
    margin: 0 auto;
    padding-top: 2rem;
  }

  .media-grid-block.full-width {
    max-width: 100%;
  }

  .media-grid-header {
    margin-bottom: 2rem;
  }

  .media-grid-header.align-left {
    text-align: left;
  }

  .media-grid-header.align-center {
    text-align: center;
  }

  .media-grid-header.align-right {
    text-align: right;
  }

  .media-grid-title {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: var(--text-color);
  }

  .media-grid-subtitle {
    font-size: 1.125rem;
    color: var(--text-color-secondary);
    opacity: 0.8;
  }

  .media-grid-content {
    margin-top: 1rem;
  }

  .media-grid-content p {
    font-size: 1rem;
    color: var(--text-color);
    line-height: 1.6;
    margin: 0;
  }

  .media-grid-container {
    position: relative;
  }

  .media-grid {
    display: grid;
    gap: var(--gap);
    grid-template-columns: repeat(var(--mobile-columns), 1fr);
  }

  .media-grid.align-center {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }

  .media-grid.align-center .media-grid-item {
    flex: 0 0 calc((100% - (var(--mobile-columns) - 1) * var(--gap)) / var(--mobile-columns));
    max-width: calc((100% - (var(--mobile-columns) - 1) * var(--gap)) / var(--mobile-columns));
  }

  @media (min-width: 640px) {
    .media-grid {
      grid-template-columns: repeat(var(--tablet-columns), 1fr);
    }

    .media-grid.align-center .media-grid-item {
      flex: 0 0 calc((100% - (var(--tablet-columns) - 1) * var(--gap)) / var(--tablet-columns));
      max-width: calc((100% - (var(--tablet-columns) - 1) * var(--gap)) / var(--tablet-columns));
    }
  }

  @media (min-width: 1024px) {
    .media-grid {
      grid-template-columns: repeat(var(--desktop-columns), 1fr);
    }

    .media-grid.align-center .media-grid-item {
      flex: 0 0 calc((100% - (var(--desktop-columns) - 1) * var(--gap)) / var(--desktop-columns));
      max-width: calc((100% - (var(--desktop-columns) - 1) * var(--gap)) / var(--desktop-columns));
    }
  }

  .media-grid-item {
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease;
  }

  .media-grid-item:hover {
    transform: scale(var(--hover-scale, 1.05));
  }

  .media-link {
    display: block;
    width: 100%;
    height: 100%;
  }

  .media-image,
  .media-video {
    width: 100%;
    height: 100%;
    display: block;
  }

  /* Carousel mode styles */
  .carousel-mode .media-grid {
    transition: opacity 0.5s ease;
  }

  .carousel-controls {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-between;
    padding: 0 1rem;
    pointer-events: none;
    transform: translateY(-50%);
  }

  .carousel-control {
    pointer-events: all;
    background: rgba(0, 0, 0, 0.5);
    color: white;
    border: none;
    border-radius: 50%;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.3s ease;
  }

  .carousel-control:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  .carousel-indicators {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 1.5rem;
  }

  .carousel-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: none;
    background: var(--border-color);
    cursor: pointer;
    transition: background 0.3s ease, width 0.3s ease;
  }

  .carousel-indicator.active {
    background: var(--primary-color);
    width: 24px;
    border-radius: 4px;
  }

  /* Dark mode support */
  :global([data-theme='dark']) .media-grid-title {
    color: var(--text-color-dark);
  }

  :global([data-theme='dark']) .media-grid-subtitle {
    color: var(--text-color-dark);
    opacity: 0.7;
  }

  :global([data-theme='dark']) .media-grid-content p {
    color: var(--text-color-dark);
  }

  :global([data-theme='dark']) .carousel-control {
    background: rgba(255, 255, 255, 0.1);
  }

  :global([data-theme='dark']) .carousel-control:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  :global([data-theme='dark']) .carousel-indicator {
    background: var(--border-color-dark);
  }
</style>